import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  HemisphereLight,
  InstancedMesh,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  Points,
  ShaderMaterial,
  SpotLight,
  Vector3,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { Quality } from '../render/pipeline';
import { ringHalf } from './ring';
import { sceneTime } from './time';

const trussHeight = 6.2;
/** The truss is a square box of tubes this deep and high. */
const trussDepth = 0.4;
const lampsPerSide = 8;
const warm = new Color(1, 0.92, 0.8);

/**
 * The light over the ring: a square truss of lamps, spotlights pouring warm light onto the canvas (one of them casting
 * soft shadows), beams visible in the haze, and dust drifting through them. Everything outside the ring stays dark.
 */
export function buildLights(quality: Quality): Group {
  const group = new Group();
  group.name = 'Lights';
  group.add(new HemisphereLight(0x2a3448, 0x120a08, 0.07));

  const half = ringHalf + 0.8;
  const truss = new Mesh(trussFrame(half), new MeshStandardMaterial({ color: 0x2a2a30, metalness: 0.85, roughness: 0.35 }));
  truss.name = 'Truss';
  group.add(truss);

  // A row of lamps hanging under the inside of each side, every one turned on the middle of the ring.
  const lampSpots: Vector3[] = [];
  for (let side = 0; side < 4; side++) {
    const turn = (side * Math.PI) / 2;
    for (let i = 0; i < lampsPerSide; i++) {
      const along = (i - (lampsPerSide - 1) / 2) * 0.95;
      const inside = half - trussDepth / 2;
      lampSpots.push(new Vector3(Math.sin(turn) * inside + Math.cos(turn) * along, trussHeight - trussDepth / 2 - 0.22, Math.cos(turn) * inside - Math.sin(turn) * along));
    }
  }
  const target = new Object3D();
  target.position.set(0, 0.8, 0);
  group.add(target);
  const housings = new InstancedMesh(new CylinderGeometry(0.14, 0.18, 0.3, 12), new MeshStandardMaterial({ color: 0x111114, metalness: 0.6, roughness: 0.5 }), lampSpots.length);
  const lenses = new InstancedMesh(new CylinderGeometry(0.13, 0.13, 0.02, 12), new MeshBasicMaterial({ color: warm.clone().multiplyScalar(12) }), lampSpots.length);
  const aim = new Object3D();
  lampSpots.forEach((spot, i) => {
    aim.position.copy(spot);
    aim.lookAt(target.position);
    aim.rotateX(Math.PI / 2);
    aim.updateMatrix();
    housings.setMatrixAt(i, aim.matrix);
    aim.position.lerp(target.position, 0.028);
    aim.updateMatrix();
    lenses.setMatrixAt(i, aim.matrix);
    if (i % lampsPerSide === Math.floor(lampsPerSide / 2)) group.add(beamOf(spot, target.position));
  });
  group.add(housings, lenses);

  // Four big spots, one per side of the truss; the one behind the player casts the shadows.
  const spots = [new Vector3(0, trussHeight, -half), new Vector3(half, trussHeight, 0), new Vector3(0, trussHeight, half), new Vector3(-half, trussHeight, 0)];
  spots.forEach((position, i) => {
    const spot = new SpotLight(warm, 170, 11, 0.44, 0.45, 2);
    spot.position.copy(position);
    spot.target = target;
    if (i === 0 && quality.shadows) {
      spot.castShadow = true;
      spot.shadow.mapSize.set(quality.shadowMapSize, quality.shadowMapSize);
      spot.shadow.bias = -0.0004;
      spot.shadow.normalBias = 0.02;
      spot.shadow.radius = 4;
      spot.shadow.camera.near = 2;
      spot.shadow.camera.far = 14;
    }
    group.add(spot);
  });

  // Cool rim lights behind the opponent, to lift him out of the dark hall; their spill falls behind the player, unseen.
  const rimTarget = new Object3D();
  rimTarget.position.set(0, 1.3, -0.3);
  group.add(rimTarget);
  for (const x of [-3.2, 3.2]) {
    const rim = new SpotLight(0x8fb4ff, 160, 9, 0.35, 0.6, 2);
    rim.position.set(x, 2.8, -4.2);
    rim.target = rimTarget;
    group.add(rim);
  }

  // A soft fill from behind the player, so the opponent's face is seen, not lost under the top light.
  const faceTarget = new Object3D();
  faceTarget.position.set(0, 1.55, -0.3);
  group.add(faceTarget);
  const fill = new SpotLight(0xffe2c8, 45, 8, 0.45, 0.9, 2);
  fill.position.set(0.4, 2.2, 3.2);
  fill.target = faceTarget;
  group.add(fill);

  group.add(dust());
  return group;
}

// The truss over the ring: on each side four tubes along its length, laced between them with diagonals on every face,
// all one shape for one draw.
function trussFrame(half: number): BufferGeometry {
  const struts: BufferGeometry[] = [];
  const strut = (from: Vector3, to: Vector3, radius: number) => {
    const length = from.distanceTo(to);
    const tube = new CylinderGeometry(radius, radius, length, 6, 1, true);
    tube.translate(0, length / 2, 0);
    const turn = new Object3D();
    turn.position.copy(from);
    turn.lookAt(to);
    turn.rotateX(Math.PI / 2);
    turn.updateMatrix();
    struts.push(tube.applyMatrix4(turn.matrix));
  };
  const d = trussDepth / 2;
  const length = half * 2 + trussDepth;
  const bays = Math.round(length / trussDepth);
  const corners = [[-d, -d], [-d, d], [d, d], [d, -d]] as const;
  for (let side = 0; side < 4; side++) {
    const turn = (side * Math.PI) / 2;
    // A point on this side: along its length, and across and up within its box.
    const at = (along: number, across: number, up: number) =>
      new Vector3(Math.sin(turn) * (half + across) + Math.cos(turn) * along, trussHeight + up, Math.cos(turn) * (half + across) - Math.sin(turn) * along);
    for (const [across, up] of corners) strut(at(-length / 2, across, up), at(length / 2, across, up), 0.028);
    for (let face = 0; face < 4; face++) {
      const [a, b] = [corners[face], corners[(face + 1) % 4]];
      for (let bay = 0; bay < bays; bay++) {
        const x0 = -length / 2 + (bay * length) / bays;
        const x1 = -length / 2 + ((bay + 1) * length) / bays;
        const [from, to] = bay % 2 ? [a, b] : [b, a];
        strut(at(x0, from[0], from[1]), at(x1, to[0], to[1]), 0.012);
      }
    }
  }
  return mergeGeometries(struts)!;
}

// A beam of light made visible by the haze: a cone that fades towards its edges and its end, and near the eye, so from
// inside one, as the player is, it does not veil the whole hall.
function beamOf(from: Vector3, to: Vector3): Mesh {
  const length = from.distanceTo(to) + 1.2;
  const geometry = new ConeGeometry(1.35, length, 32, 1, true);
  geometry.translate(0, -length / 2, 0);
  const material = new ShaderMaterial({
    uniforms: { colour: { value: warm.clone().multiplyScalar(0.12) } },
    vertexShader: `
      varying float vAlong;
      varying vec3 vNormal;
      varying vec3 vView;
      varying float vDistance;
      void main() {
        vAlong = -position.y / ${length.toFixed(2)};
        vec4 world = modelViewMatrix * vec4(position, 1.0);
        vNormal = normalize(normalMatrix * normal);
        vView = normalize(-world.xyz);
        vDistance = length(world.xyz);
        gl_Position = projectionMatrix * world;
      }`,
    fragmentShader: `
      uniform vec3 colour;
      varying float vAlong;
      varying vec3 vNormal;
      varying vec3 vView;
      varying float vDistance;
      void main() {
        float edge = pow(abs(dot(normalize(vNormal), normalize(vView))), 2.0);
        float fade = (1.0 - vAlong) * smoothstep(0.0, 0.08, vAlong) * smoothstep(2.0, 7.0, vDistance);
        gl_FragColor = vec4(colour * edge * fade, 1.0);
      }`,
    blending: AdditiveBlending,
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
  });
  const beam = new Mesh(geometry, material);
  beam.position.copy(from);
  beam.lookAt(to);
  beam.rotateX(-Math.PI / 2);
  beam.name = 'Light beam';
  return beam;
}

// Dust drifting slowly in the light above the ring.
function dust(): Points {
  const count = 1400;
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    positions.set([(Math.random() - 0.5) * 9, Math.random() * 6, (Math.random() - 0.5) * 9], i * 3);
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  const material = new ShaderMaterial({
    uniforms: { time: sceneTime },
    vertexShader: `
      uniform float time;
      varying float vLight;
      void main() {
        vec3 p = position;
        p.x += sin(time * 0.13 + position.y * 1.7) * 0.25;
        p.z += cos(time * 0.11 + position.x * 1.3) * 0.25;
        p.y = mod(p.y + time * 0.05, 6.0);
        vLight = smoothstep(4.5, 1.0, length(p.xz));
        vec4 view = modelViewMatrix * vec4(p, 1.0);
        gl_Position = projectionMatrix * view;
        gl_PointSize = 22.0 / -view.z;
      }`,
    fragmentShader: `
      varying float vLight;
      void main() {
        float d = length(gl_PointCoord - 0.5);
        gl_FragColor = vec4(vec3(1.0, 0.9, 0.75) * 0.5 * vLight * smoothstep(0.5, 0.1, d), 1.0);
      }`,
    blending: AdditiveBlending,
    transparent: true,
    depthWrite: false,
  });
  const points = new Points(geometry, material);
  points.frustumCulled = false;
  points.name = 'Dust';
  return points;
}
